import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Select,
  Textarea,
  useColorModeValue,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Icon,
  Flex,
  Divider,
  Progress,
  Alert,
  AlertIcon,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Avatar,
  Tooltip
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdCancel,
  MdPending,
  MdAccessTime,
  MdComment,
  MdVisibility,
  MdFileDownload,
  MdSave,
  MdWarning
} from 'react-icons/md';
import { FaFileAlt, FaCheck, FaTimes } from 'react-icons/fa';
import api from '../../../../../services/api';
import documentService from '../../../../../services/documentService';

const DocumentAnalysisPanel = ({ isOpen, onClose, assignment, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [documentComments, setDocumentComments] = useState({});
  const [savingDoc, setSavingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (isOpen && assignment) {
      fetchAssignmentDetails();
    }
  }, [isOpen, assignment]);

  const fetchAssignmentDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/juridico/assignment/${assignment._id}`);
      console.log('Assignment Details Response:', response);
      
      // Handle both response structures
      const responseData = response.data || response || {};
      const assignmentData = responseData.data || responseData || {};
      console.log('Setting assignment details to:', assignmentData);
      setAssignmentDetails(assignmentData);
      
      // Initialize document statuses
      const statuses = {};
      const comments = {};
      if (assignmentData.assignment && assignmentData.assignment.documentAnalysis) {
        Object.keys(assignmentData.assignment.documentAnalysis).forEach(doc => {
          statuses[doc] = assignmentData.assignment.documentAnalysis[doc].status;
          comments[doc] = '';
        });
      }
      setDocumentStatuses(statuses);
      setDocumentComments(comments);
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      toast({
        title: 'Erro ao carregar detalhes',
        description: 'Não foi possível carregar os detalhes do precatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getDocumentLabel = (docType) => {
    const labels = {
      rg_cpf_cnh: 'RG, CPF ou CNH',
      comprovante_residencia: 'Comprovante de Residência',
      certidao_casamento: 'Certidão de Casamento',
      dados_bancarios: 'Dados Bancários',
      contrato_honorarios: 'Contrato de Honorários',
      selfie_documento: 'Selfie com Documento',
      certidao_estadual: 'Certidão Estadual',
      certidao_federal: 'Certidão Federal',
      contrato_social: 'Contrato Social',
      certidao_inteiro_teor: 'Certidão de Inteiro Teor',
      rg_cpf_socios: 'RG/CPF dos Sócios',
      comprovante_endereco_empresa: 'Comprovante Endereço Empresa',
      dados_bancarios_empresa: 'Dados Bancários Empresa'
    };
    return labels[docType] || docType;
  };

  const getStatusColor = (status) => {
    const colors = {
      pendente: 'gray',
      em_analise: 'blue',
      aprovado: 'green',
      reprovado: 'red',
      nao_aplicavel: 'gray'
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendente: MdPending,
      em_analise: MdAccessTime,
      aprovado: MdCheckCircle,
      reprovado: MdCancel,
      nao_aplicavel: MdWarning
    };
    return icons[status] || MdPending;
  };

  const handleStatusUpdate = async (docType) => {
    try {
      setSavingDoc(docType);
      
      const payload = {
        status: documentStatuses[docType]
      };
      
      if (documentComments[docType]) {
        payload.comment = {
          texto: documentComments[docType],
          tipo: documentStatuses[docType] === 'reprovado' ? 'correcao_necessaria' : 'validacao'
        };
      }
      
      await api.put(`/juridico/assignment/${assignment._id}/document/${docType}/status`, payload);
      
      toast({
        title: 'Status atualizado',
        description: `Status do documento ${getDocumentLabel(docType)} foi atualizado`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh details
      fetchAssignmentDetails();
      
      // Clear comment after save
      setDocumentComments(prev => ({ ...prev, [docType]: '' }));
      
    } catch (error) {
      console.error('Error updating document status:', error);
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível atualizar o status do documento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingDoc(null);
    }
  };

  const handleViewDocument = async (docType) => {
    try {
      setViewingDoc(docType);
      const response = await documentService.getDocumentUrl(
        assignment.estado,
        assignment.contactedPrecatorioId,
        docType
      );
      
      if (response.success && response.data) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast({
        title: 'Erro ao visualizar',
        description: 'Não foi possível abrir o documento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setViewingDoc(null);
    }
  };

  const isDocumentAvailable = (docType) => {
    return assignmentDetails?.documents?.[docType]?.uploaded;
  };

  const getApplicableDocuments = () => {
    if (!assignmentDetails?.assignment) return [];
    
    return Object.entries(assignmentDetails.assignment.documentAnalysis)
      .filter(([_, doc]) => doc.status !== 'nao_aplicavel')
      .map(([key, doc]) => ({ key, ...doc }));
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || !assignmentDetails) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Análise de Documentos</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex justify="center" align="center" h="400px">
              <Spinner size="xl" />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const applicableDocuments = getApplicableDocuments();
  const progress = assignmentDetails.assignment.analysisProgress;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text>Análise de Documentos - {assignment.numero_precatorio}</Text>
              <Badge colorScheme="purple" fontSize="md">
                {assignment.estado}
              </Badge>
            </HStack>
            <Box>
              <Text fontSize="sm" color={mutedColor}>
                Beneficiário: {assignment.nome_beneficiario} ({assignment.cpf_cnpj})
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color={mutedColor} mb={2}>
                Progresso da Análise: {progress?.percentComplete || 0}%
              </Text>
              <Progress 
                value={progress?.percentComplete || 0} 
                size="sm" 
                colorScheme={progress?.percentComplete === 100 ? 'green' : 'blue'}
                borderRadius="full"
              />
              <HStack spacing={4} mt={2}>
                <Text fontSize="xs" color={mutedColor}>
                  Analisados: {progress?.analyzedDocuments || 0}/{progress?.totalDocuments || 0}
                </Text>
                <Text fontSize="xs" color="green.500">
                  Aprovados: {progress?.approvedDocuments || 0}
                </Text>
                <Text fontSize="xs" color="red.500">
                  Reprovados: {progress?.reprovedDocuments || 0}
                </Text>
              </HStack>
            </Box>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody overflowY="auto" maxH="calc(90vh - 200px)">
          <VStack spacing={4} align="stretch">
            {applicableDocuments.map((doc) => (
              <Box
                key={doc.key}
                p={4}
                borderWidth={1}
                borderColor={borderColor}
                borderRadius="md"
                bg={bgColor}
              >
                <Grid templateColumns="repeat(12, 1fr)" gap={4} alignItems="center">
                  <GridItem colSpan={{ base: 12, md: 4 }}>
                    <HStack spacing={3}>
                      <Icon 
                        as={getStatusIcon(documentStatuses[doc.key])} 
                        color={`${getStatusColor(documentStatuses[doc.key])}.500`}
                        boxSize={6}
                      />
                      <Box flex={1}>
                        <Text fontWeight="semibold">
                          {getDocumentLabel(doc.key)}
                        </Text>
                        {doc.analyzedAt && (
                          <Text fontSize="xs" color={mutedColor}>
                            Analisado em: {formatDate(doc.analyzedAt)}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  </GridItem>
                  
                  <GridItem colSpan={{ base: 12, md: 3 }}>
                    <Select
                      size="sm"
                      value={documentStatuses[doc.key]}
                      onChange={(e) => setDocumentStatuses(prev => ({
                        ...prev,
                        [doc.key]: e.target.value
                      }))}
                    >
                      <option value="pendente">Pendente</option>
                      <option value="em_analise">Em Análise</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="reprovado">Reprovado</option>
                    </Select>
                  </GridItem>
                  
                  <GridItem colSpan={{ base: 12, md: 5 }}>
                    <HStack spacing={2}>
                      {isDocumentAvailable(doc.key) ? (
                        <Button
                          size="sm"
                          leftIcon={<MdVisibility />}
                          onClick={() => handleViewDocument(doc.key)}
                          isLoading={viewingDoc === doc.key}
                          variant="outline"
                        >
                          Visualizar
                        </Button>
                      ) : (
                        <Badge colorScheme="red">Documento não enviado</Badge>
                      )}
                      
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<MdSave />}
                        onClick={() => handleStatusUpdate(doc.key)}
                        isLoading={savingDoc === doc.key}
                      >
                        Salvar
                      </Button>
                    </HStack>
                  </GridItem>
                  
                  <GridItem colSpan={12}>
                    <Textarea
                      size="sm"
                      placeholder="Adicione um comentário sobre este documento..."
                      value={documentComments[doc.key]}
                      onChange={(e) => setDocumentComments(prev => ({
                        ...prev,
                        [doc.key]: e.target.value
                      }))}
                      rows={2}
                    />
                  </GridItem>
                  
                  {doc.comments && doc.comments.length > 0 && (
                    <GridItem colSpan={12}>
                      <Accordion allowMultiple size="sm">
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <Text fontSize="sm">
                                <Icon as={MdComment} mr={2} />
                                Comentários ({doc.comments.length})
                              </Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <VStack align="stretch" spacing={2}>
                              {doc.comments.map((comment, idx) => (
                                <Box
                                  key={idx}
                                  p={2}
                                  bg="gray.50"
                                  borderRadius="md"
                                  fontSize="sm"
                                >
                                  <HStack justify="space-between" mb={1}>
                                    <HStack>
                                      <Avatar size="xs" name={comment.usuario?.name} />
                                      <Text fontWeight="semibold">
                                        {comment.usuario?.name || 'Usuário'}
                                      </Text>
                                    </HStack>
                                    <Badge colorScheme={
                                      comment.tipo === 'correcao_necessaria' ? 'red' : 
                                      comment.tipo === 'aprovacao' ? 'green' : 'blue'
                                    }>
                                      {comment.tipo}
                                    </Badge>
                                  </HStack>
                                  <Text>{comment.texto}</Text>
                                  <Text fontSize="xs" color={mutedColor}>
                                    {formatDate(comment.data)}
                                  </Text>
                                </Box>
                              ))}
                            </VStack>
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </GridItem>
                  )}
                </Grid>
              </Box>
            ))}
            
            {progress?.percentComplete === 100 && (
              <Alert status="success">
                <AlertIcon />
                Todos os documentos foram analisados. Você pode emitir o parecer jurídico.
              </Alert>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DocumentAnalysisPanel;